/**
 * What a published web bundle is, and how a device decides the bytes it has are
 * the bytes that were built.
 *
 * A bundle is **a manifest plus the files it names**, not an archive. That is a
 * deliberate choice rather than a shortcut:
 *
 *  - The file list is the security boundary. Every file is hashed and sized
 *    individually, so a truncated download, a half-written swap and a tampered
 *    asset are three distinguishable failures naming one path each, where an
 *    archive would give one opaque blob and a byte count.
 *  - Android already has ZIP and nothing else: an archive would mean a tar or zip
 *    reader on the device, in Kotlin, with its own bugs. A directory of files is
 *    written with `FileOutputStream` and read by the WebView with no format code
 *    on either side.
 *  - The build output is already a directory with content-hashed asset names, so
 *    publishing is a copy plus a hash pass, and a rebuild that changes nothing
 *    publishes the same version (see `artifactVersion`) and can be a no-op.
 *
 * The version is a content address, so "is there an update?" is a string compare
 * the device does before it downloads anything. The manifest is data, so
 * `parseManifest` refuses to trust the shape of what came off the wire: a client
 * that assumed `files` was an array would crash on a captive-portal HTML page
 * instead of reporting "the update server answered with nonsense".
 */
/**
 * What a published artifact is: the app itself, or the catalog data a scan needs.
 *
 * Two kinds, because the two lifecycles are different - the SPA changes when the
 * code does, the art index when the catalog does - but the wire format and the
 * verification are the same, so a device has one update mechanism rather than two.
 * The kind is carried by the version's prefix rather than by a field beside it:
 * the version is already the answer to "which artifact is this", and a second
 * field carrying the same answer is a second field that can disagree.
 */
export const ARTIFACT_KINDS = ['web', 'art'];
/**
 * SHA-256 through WebCrypto, which is the same call in a browser and in Node.
 *
 * Not `node:crypto`: this function runs on the device too, inside the web bundle,
 * and a module that imports `node:crypto` cannot be bundled for a browser. The
 * global is read off `globalThis` rather than imported so the type does not depend
 * on a DOM lib either.
 */
export const sha256Hex = async (bytes) => {
    const subtle = globalThis
        .crypto?.subtle;
    if (!subtle?.digest)
        throw new Error('no WebCrypto implementation: cannot hash a bundle');
    const digest = await subtle.digest('SHA-256', bytes);
    return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
};
/**
 * The version a file list names, for one kind of artifact.
 *
 * Sorted and length-prefixed, so two builds that emit the same files in a
 * different order are one version, and a file whose *name* changes is a new
 * version even when its bytes do not.
 *
 * The kind is part of the version rather than a field beside it, so an app release
 * and a catalog artifact of the same bytes are different versions - which is what
 * lets a device refuse one when it asked for the other.
 */
export async function artifactVersion(kind, files, hash = sha256Hex) {
    const canonical = [...files]
        .sort((left, right) => (left.path < right.path ? -1 : left.path > right.path ? 1 : 0))
        .map((file) => `${file.path}\u0000${file.sha256}\u0000${file.bytes}\n`)
        .join('');
    return `${kind}-${(await hash(new TextEncoder().encode(canonical))).slice(0, 12)}`;
}
export const fileCount = (manifest) => manifest.files.length;
export const totalBytes = (manifest) => manifest.files.reduce((sum, file) => sum + file.bytes, 0);
/**
 * A path a bundle may contain.
 *
 * The manifest is untrusted input and it names files that get written to disk, so
 * `..`, an absolute path, a backslash or a NUL is refused before anything is
 * created. This is the check that makes `files[].path` safe to hand to a
 * filesystem, and it is refused on both sides: the client refuses to write it, and
 * the API refuses to serve it.
 */
export function isSafeBundlePath(path) {
    if (path === '' || path.length > 512)
        return false;
    if (path.startsWith('/') || path.includes('\\') || path.includes('\u0000'))
        return false;
    return path.split('/').every((segment) => segment !== '' && segment !== '.' && segment !== '..');
}
/**
 * How a downloaded bundle failed, as sentences naming the files.
 *
 * Empty means intact. Problems are listed per file rather than as one boolean so
 * the reason can be shown, logged and asserted on: "index.html is 4021 bytes,
 * not 12044" is a diagnosis, and "the bundle is invalid" is not.
 */
export async function verifyBundle(source, manifest, hash = sha256Hex) {
    const problems = [];
    const present = new Set(await source.list());
    const described = new Set();
    for (const file of manifest.files) {
        described.add(file.path);
        if (!isSafeBundlePath(file.path)) {
            problems.push(`unsafe ${file.path}`);
            continue;
        }
        if (!present.has(file.path)) {
            problems.push(`missing ${file.path}`);
            continue;
        }
        const bytes = await source.read(file.path);
        if (bytes.byteLength !== file.bytes) {
            problems.push(`${file.path}: ${bytes.byteLength} bytes, not ${file.bytes}`);
            continue;
        }
        const digest = await hash(bytes);
        if (digest !== file.sha256)
            problems.push(`${file.path}: sha256 ${digest}, not ${file.sha256}`);
    }
    for (const path of [...present].sort()) {
        if (!described.has(path))
            problems.push(`unexpected ${path}`);
    }
    return problems;
}
/** The one sentence a screen or a log shows for a set of problems. */
export function describeProblems(problems) {
    if (problems.length === 0)
        return 'the bundle verified against its manifest';
    const shown = problems.slice(0, 3).join('; ');
    return `${problems.length} problem(s) with the downloaded bundle: ${shown}${problems.length > 3 ? ` and ${problems.length - 3} more` : ''}`;
}
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
/** What a version string looks like: a kind from `ARTIFACT_KINDS`, then a digest. */
const VERSION_PATTERN = new RegExp(`^(?:${ARTIFACT_KINDS.join('|')})-[0-9a-f]{4,64}$`);
/**
 * A manifest read off the wire, or an error saying what is wrong with it.
 *
 * Validated field by field rather than cast, because the caller is about to write
 * these paths to a filesystem and compare these digests: a captive portal's login
 * page parsed as JSON would otherwise become a bundle with no files, which verifies
 * as empty and would be activated as the app.
 *
 * The `kind` is what the caller asked for, and a manifest naming another kind is
 * refused. The two artifacts share this format and its machinery, so this is the
 * one check that keeps a device from installing the catalog's data as the app it
 * boots - and it costs a string comparison the version already carries.
 */
export function parseManifest(value, kind) {
    if (!isRecord(value))
        throw new Error('the update manifest is not an object');
    const { version, builtAt, bytes, files } = value;
    if (typeof version !== 'string' || !VERSION_PATTERN.test(version)) {
        throw new Error('the update manifest has no usable version');
    }
    if (!version.startsWith(`${kind}-`)) {
        throw new Error(`the update manifest is ${version}, and a ${kind} artifact was asked for`);
    }
    if (typeof builtAt !== 'string')
        throw new Error('the update manifest has no builtAt');
    if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) {
        throw new Error('the update manifest has no usable byte count');
    }
    if (!Array.isArray(files) || files.length === 0) {
        throw new Error('the update manifest names no files');
    }
    const parsed = files.map((entry, index) => {
        if (!isRecord(entry))
            throw new Error(`file ${index} of the update manifest is not an object`);
        const { path, sha256, bytes: size } = entry;
        if (typeof path !== 'string' || !isSafeBundlePath(path)) {
            throw new Error(`file ${index} of the update manifest has an unusable path`);
        }
        if (typeof sha256 !== 'string' || !/^[0-9a-f]{64}$/.test(sha256)) {
            throw new Error(`${path} has no sha256 in the update manifest`);
        }
        if (typeof size !== 'number' || !Number.isInteger(size) || size < 0) {
            throw new Error(`${path} has no usable size in the update manifest`);
        }
        return { path, sha256, bytes: size };
    });
    return { version, builtAt, bytes, files: parsed };
}
//# sourceMappingURL=manifest.js.map